/**
 * Mirrors the core app's palette in `frontend/src/styles/variables.ts` (and its CSS twin,
 * `frontend/src/styles/ediphiColorPalette.scss`). The add-in is a standalone npm package and cannot
 * import from `frontend/`, so the values are kept in sync by hand — the same arrangement those two
 * files already have with each other.
 *
 * **If you change a value in `ediphiColors`, check it against `frontend/src/styles/variables.ts`.**
 * Only the steps the add-in actually uses are mirrored, under the core app's own names.
 */
const ediphiColors = {
  gray0: "#ffffff",
  gray50: "#fafafa",
  gray100: "#f5f5f5",
  gray200: "#e6e6e6",
  gray300: "#cdcdcd",
  gray400: "#b4b4b4",
  gray500: "#9b9b9b",
  gray600: "#6a6a6a",
  gray800: "#212121",
  gray900: "#060606",

  brand25: "#f9fbfe",
  brand50: "#e6effd",
  brand100: "#ccdffa",
  brand200: "#b3d0f8",
  brand300: "#99c0f6",
  brand400: "#67a0f1",
  brand500: "#3481ed",
  brand600: "#0161e8",
  brand700: "#014eba",
  brand800: "#013a8b",
  brand900: "#00275d",

  blueGray50: "#f9fafb",
  blueGray75: "#f2f5fa",
  blueGray100: "#f0f3f6",
  blueGray300: "#d0d5dd",

  error50: "#fee7e9",
  error500: "#ed3d4e",
  error600: "#e80c22",
  error800: "#8b0714",

  warning50: "#fef9e9",
  warning500: "#f8cc51",
  warning600: "#f6bf25",
  warning900: "#624c0f",

  success50: "#ccfcff",
  success600: "#005e63",
  success800: "#00383b",

  info50: "#e1f5ff",
  info600: "#017db7",
  info800: "#014b6e",
} as const;

/**
 * Values that are NOT in `ediphiColors`. Each is close to one, so each is probably a hand-typed
 * near-miss rather than a decision — but changing them moves pixels, so they are listed here to be
 * settled deliberately rather than silently folded in.
 *
 * The `Δ` is the summed RGB channel distance from the nearest `ediphiColors` step.
 */
const driftColors = {
  white: "#fefeff", //                     Δ2  from gray0 #ffffff
  secondaryBorderColor: "#e5e5e5", //      Δ3  from gray200 #e6e6e6
  ediphiPrimaryButtonBorder: "#b3b3b3", // Δ3  from gray400 #b4b4b4
  borderDivider: "#e8e8e8", //             Δ6  from gray200 #e6e6e6
  greyOut: "#f0f0f0", //                   Δ9  from blueGray100 #f0f3f6
  ediphiTextButtonBgActive: "#e0ecfc", //  Δ10 from brand50 #e6effd
  ediphiTextButtonBgHover: "#ebf3fe", //   Δ10 from brand50 #e6effd
  ediphiSelectBorder: "#dfe2e5", //        Δ12 from gray200 #e6e6e6
  babyBlue: "#7ec8e3", //                  Δ13 from info400 #87c5e2
  ediphiTextButtonDisabledColor: "#d7d7d7", // Δ15 from blueGray300 #d0d5dd
  ediphiTabButtonBorder: "#d9d9d9", //     Δ17 from blueGray300 #d0d5dd
  ediphiInputPlaceholderColor: "#d9d9d9", // Δ17 from blueGray300 #d0d5dd
  textBlack: "#000000", //                 Δ18 from gray900 #060606
  ediphiPromptTextColor: "#646262", //     Δ22 from gray600 #6a6a6a
  mediumBlue: "#000c66", //                Δ23 from ultramarine900 #100566
  accent: "#0055cc", //                    Δ26 from brand700 #014eba
  black900: "#161616", //                  Δ26 from blueGray900 #101828
  mainTextColor: "#111", //                Δ31 from gray900 #060606      — all body text
  darkBlue: "#050a30", //                  Δ33 from blueGray900 #101828
  greyText: "#757575", //                  Δ33 from gray600 #6a6a6a      — theme text.secondary
  ediphiPrimaryLightblue: "#156dea", //    Δ34 from brand600 #0161e8
  justBlue: "#0000ff", //                  Δ53 from ultramarine600 #290cff
  mainGreen: "#32b186", //                 Δ54 from success400 #389ca1   — theme success.main
  secondaryRed: "#ED3D4Eb0", //            error500 at b0 alpha
} as const;

export const ediphiPalette = {
  ...ediphiColors,
  ...driftColors,

  /**
   * Role names. These are what call sites should use: the name says what the colour is for, so a
   * value can be repointed at a different `ediphiColors` step without touching every consumer.
   *
   * `surface` is pure white and is NOT `white` above, which is the off-white #fefeff. The two are
   * not interchangeable.
   */
  surface: ediphiColors.gray0,
  surfaceSubtle: ediphiColors.gray50,
  surfaceDisabled: ediphiColors.gray100,
  /**
   * The disabled fill for a button that is *filled* when enabled, where the fill is what makes it
   * read as a control. Darker than `surfaceDisabled` so it still separates from the page background,
   * which `surfaceDisabled` sits five units from. An outlined button keeps `surfaceDisabled` — its
   * border does that job instead.
   */
  surfaceDisabledFilled: ediphiColors.gray200,

  borderColor: ediphiColors.gray200,
  borderStrong: ediphiColors.gray300,
  borderInput: ediphiColors.blueGray300,

  textStrong: ediphiColors.gray800,
  textMuted: ediphiColors.gray600,
  textDisabled: ediphiColors.gray400,
  textOnAccent: ediphiColors.gray0,

  mainBackground: ediphiColors.blueGray100,
  mainRed: ediphiColors.error500,
  mainYellow: ediphiColors.warning500,

  /* Aliases kept for existing call sites; each is the `ediphiColors` step named beside it. */
  gridAccent: ediphiColors.brand600,
  ediphiPrimaryBlue: ediphiColors.brand600,
  ediphiButtonDisabledColor: ediphiColors.brand100,
  ediphiListItemHover: ediphiColors.blueGray50,
  black950: ediphiColors.gray900,
};
